/**
 * Cheap stateless outdoor light effects (+field), all animated on the GPU from U.uTime:
 *  - GlowCards: camera-facing additive halos (lantern panes, crystals, runes, embers) in one
 *    instanced draw;
 *  - Motes: drifting / rising points around a centre (portal sparkles, barrow wisps, fireflies);
 *  - swirlMaterial: the portal vortex disc.
 * Brightness stays moderate (art direction): they bloom a little, they never white out.
 */
import * as THREE from 'three';
import { NOISE_GLSL, U } from '../style';

export interface GlowSpec {
  /** three.js world position. */
  x: number;
  y: number;
  z: number;
  size: number;
  color: readonly [number, number, number];
  /** Pulse amount 0..1 (0 = steady). */
  pulse?: number;
}

export function glowCards(specs: readonly GlowSpec[]): THREE.InstancedMesh | null {
  if (!specs.length) return null;
  const geo = new THREE.PlaneGeometry(1, 1);
  const color = new Float32Array(specs.length * 4);
  specs.forEach((s, i) => {
    color[i * 4] = s.color[0];
    color[i * 4 + 1] = s.color[1];
    color[i * 4 + 2] = s.color[2];
    color[i * 4 + 3] = s.pulse ?? 0.15;
  });
  geo.setAttribute('aColor', new THREE.InstancedBufferAttribute(color, 4));
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx },
    vertexShader: `attribute vec4 aColor; varying vec2 vUv; varying vec4 vColor; varying float vSeed;
      void main(){
        vUv = uv; vColor = aColor;
        vec4 c = instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
        float s = length(instanceMatrix[0].xyz);
        vSeed = fract(c.x * 0.173 + c.z * 0.291) * 30.0;
        vec4 mv = modelViewMatrix * c;
        mv.xy += position.xy * s;
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `uniform float uTime; uniform float uFx; varying vec2 vUv; varying vec4 vColor; varying float vSeed;
      void main(){
        float r = length(vUv * 2.0 - 1.0); if (r > 1.0) discard;
        float f = pow(max(0.0, 1.0 - r), 2.4) + 0.6 * pow(max(0.0, 1.0 - r * 3.0), 2.0);
        float p = 1.0 - vColor.a * (0.5 + 0.5 * sin(uTime * 2.3 + vSeed));
        float a = f * p * min(1.2, uFx);
        gl_FragColor = vec4(vColor.rgb * a, a);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const m = new THREE.InstancedMesh(geo, mat, specs.length);
  const M = new THREE.Matrix4();
  specs.forEach((s, i) => m.setMatrixAt(i, M.makeScale(s.size, s.size, s.size).setPosition(s.x, s.y, s.z)));
  m.frustumCulled = false;
  m.renderOrder = 9;
  m.name = 'glow-cards';
  return m;
}

/**
 * Motes around a centre: each point orbits / rises on its own seed, wrapping over `height`.
 * `mode` 0 = rise in a column (portals), 1 = wander in a disc (fireflies, dust).
 */
export function motes(n: number, radius: number, height: number, color: readonly [number, number, number], size = 0.22, mode = 0, speed = 1): THREE.Points {
  const pos = new Float32Array(n * 3);
  const seed = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) {
    const h = (k: number): number => {
      const s = Math.sin((i + 1) * 12.9898 + k * 78.233) * 43758.5453;
      return s - Math.floor(s);
    };
    seed[i * 4] = h(1);
    seed[i * 4 + 1] = h(2);
    seed[i * 4 + 2] = h(3);
    seed[i * 4 + 3] = h(4);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 4));
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, height / 2, 0), Math.max(radius, height) + 2);
  const mat = new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx, uR: { value: radius }, uH: { value: height }, uColor: { value: new THREE.Vector3(...color) }, uSize: { value: size }, uMode: { value: mode }, uSpeed: { value: speed } },
    vertexShader: `attribute vec4 aSeed; uniform float uTime; uniform float uR; uniform float uH; uniform float uSize; uniform float uMode; uniform float uSpeed; varying float vA;
      void main(){
        float t = uTime * uSpeed;
        vec3 p;
        if (uMode < 0.5) {
          float life = fract(t * (0.08 + 0.1 * aSeed.y) + aSeed.x);
          float a = aSeed.z * 6.2831 + t * (0.4 + aSeed.w * 0.6);
          float r = uR * (0.25 + 0.75 * sqrt(max(0.0, aSeed.w))) * (1.0 - 0.35 * life);
          p = vec3(cos(a) * r, life * uH, sin(a) * r);
          vA = sin(life * 3.14159);
        } else {
          float a = aSeed.z * 6.2831 + t * 0.15 * (aSeed.y - 0.5);
          float r = uR * sqrt(max(0.0, aSeed.x));
          p = vec3(cos(a) * r + sin(t * 0.7 + aSeed.w * 9.0) * 0.6, 0.4 + aSeed.w * uH + sin(t * 1.3 + aSeed.x * 7.0) * 0.4, sin(a) * r + cos(t * 0.6 + aSeed.y * 9.0) * 0.6);
          vA = 0.5 + 0.5 * sin(t * 2.5 + aSeed.y * 20.0);
        }
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = uSize * 600.0 / max(1.0, -mv.z);
        gl_Position = projectionMatrix * mv;
      }`,
    fragmentShader: `uniform vec3 uColor; uniform float uFx; varying float vA;
      void main(){ vec2 d = gl_PointCoord * 2.0 - 1.0; float r = dot(d, d); if (r > 1.0) discard;
        float f = pow(max(0.0, 1.0 - r), 2.0) * vA * min(1.2, uFx);
        gl_FragColor = vec4(uColor * f, f); }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const pts = new THREE.Points(geo, mat);
  pts.renderOrder = 9;
  return pts;
}

/** The portal vortex: spiral arms over noise, a bright core and a rim (additive, on a disc). */
export function swirlMaterial(a: readonly [number, number, number], b: readonly [number, number, number], strength = 1): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx, uA: { value: new THREE.Vector3(...a) }, uB: { value: new THREE.Vector3(...b) }, uK: { value: strength } },
    vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform float uTime; uniform float uFx; uniform vec3 uA; uniform vec3 uB; uniform float uK; varying vec2 vUv;
      ${NOISE_GLSL}
      void main(){
        vec2 p = vUv * 2.0 - 1.0; float r = length(p); if (r > 1.0) discard;
        float ang = atan(p.y, p.x);
        float sw = ang * 3.0 + r * 9.0 - uTime * 2.6;
        float arms = 0.5 + 0.5 * sin(sw);
        float n = noise(vec2(ang * 1.6 + uTime * 0.4, r * 5.0 - uTime * 1.7));
        float core = smoothstep(0.55, 0.0, r);
        float rim = smoothstep(0.72, 0.96, r) * (1.0 - smoothstep(0.96, 1.0, r));
        vec3 col = mix(uB, uA, clamp(arms * (0.4 + 0.8 * n), 0.0, 1.0));
        col = mix(col, vec3(1.0, 0.98, 0.92), core * 0.45);
        float alpha = (0.42 * arms * (0.55 + 0.45 * n) * (1.0 - r * 0.55) + core * 0.55 + rim * 0.8) * uK * min(1.2, uFx);
        gl_FragColor = vec4(col * alpha, alpha);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
}

/** Flat additive ground glow (portal footprints, rune circles). `rune` adds a ring of ticks. */
export function groundGlowMaterial(color: readonly [number, number, number], strength = 0.6, rune = false): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { uTime: U.uTime, uFx: U.uFx, uC: { value: new THREE.Vector3(...color) }, uK: { value: strength }, uRune: { value: rune ? 1 : 0 } },
    vertexShader: `varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform float uTime; uniform float uFx; uniform vec3 uC; uniform float uK; uniform float uRune; varying vec2 vUv;
      void main(){
        vec2 p = vUv * 2.0 - 1.0; float r = length(p); if (r > 1.0) discard;
        float f = pow(max(0.0, 1.0 - r), 2.0) * 0.6;
        if (uRune > 0.5) {
          float ring = smoothstep(0.03, 0.0, abs(r - 0.82)) + smoothstep(0.02, 0.0, abs(r - 0.68));
          float ang = atan(p.y, p.x) + uTime * 0.15;
          float ticks = step(0.75, fract(ang * 24.0 / 6.2831)) * step(0.69, r) * step(r, 0.81);
          f += (ring + ticks * 0.8) * 0.7;
        }
        f *= uK * (0.85 + 0.15 * sin(uTime * 1.7)) * min(1.2, uFx);
        gl_FragColor = vec4(uC * f, f);
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}
